import { useCallback, useEffect, useState } from "react";
import { Activity, AlertTriangle, KeyRound, Plus, Power, Save, Target as TargetIcon, Trash2 } from "lucide-react";
import { AttemptPill } from "@/components/StatusPill";
import { DateTimePicker } from "@/components/ui/date-picker";
import {
  clockTime,
  countdown,
  displayDate,
  formatDuration,
  shiftIso,
  stampTime,
  toApiDate,
  todayIso,
  weekdayShort,
} from "@/lib/fmt";
import type { NextExecutionInfo } from "@/components/TrainingTab";

type Schedule = Record<string, string>;

interface Target {
  id: number;
  activity_name: string;
  schedule: string;
  enabled: number;
}

interface Override {
  id: number;
  target_id: number;
  date: string;
  time: string;
  skip: number;
  created_at: string;
}

interface Attempt {
  id: number;
  target_date: string;
  activity_name: string;
  started_at: string;
  status: string;
  duration_ms: number | null;
  response_summary: string | null;
  error: string | null;
  retry_count: number;
  run_count: number;
}

export interface AutomationStatus {
  credential: { email: string; tokenExp: number | null; hasToken: boolean } | null;
  nextExecution: NextExecutionInfo | null;
  recentAttempts: Attempt[];
}

const WEEKDAYS: Array<{ key: string; short: string }> = [
  { key: "1", short: "Seg" },
  { key: "2", short: "Ter" },
  { key: "3", short: "Qua" },
  { key: "4", short: "Qui" },
  { key: "5", short: "Sex" },
  { key: "6", short: "Sáb" },
  { key: "0", short: "Dom" },
];

function parseSchedule(raw: string): Schedule {
  try {
    const parsed = JSON.parse(raw) as unknown;
    if (parsed && typeof parsed === "object") {
      const out: Schedule = {};
      for (const [k, v] of Object.entries(parsed as Record<string, unknown>)) {
        if (typeof v === "string") out[k] = v;
      }
      return out;
    }
  } catch {
    /* fall through to empty schedule */
  }
  return {};
}

function retryLabel(a: Attempt): string | null {
  const total = Math.max(0, a.run_count - 1) + Math.max(0, a.retry_count - 1);
  if (total <= 0) return null;
  return total === 1 ? "1 retry" : `${total} retries`;
}

export function AutomationTab({
  status,
  onChanged,
}: {
  status: AutomationStatus;
  onChanged: () => void;
}) {
  const [target, setTarget] = useState<Target | null>(null);
  const [overrides, setOverrides] = useState<Override[]>([]);
  const [attempts, setAttempts] = useState<Attempt[]>([]);
  const [activityName, setActivityName] = useState("");
  const [schedule, setSchedule] = useState<Schedule>({});
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const [now, setNow] = useState(() => Date.now());
  const [overrideDate, setOverrideDate] = useState(() => shiftIso(todayIso(), 1));
  const [overrideTime, setOverrideTime] = useState("06:00");
  const [overrideSkip, setOverrideSkip] = useState(false);

  const loadAttempts = useCallback(async () => {
    const res = await fetch("/api/attempts?limit=25");
    if (!res.ok) return;
    const json = (await res.json()) as Attempt[];
    setAttempts(Array.isArray(json) ? json : []);
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch("/api/targets");
      const list = (await res.json()) as Target[];
      const first = Array.isArray(list) ? (list[0] ?? null) : null;
      setTarget(first);
      setActivityName(first?.activity_name ?? "");
      setSchedule(first ? parseSchedule(first.schedule) : { "1": "06:00", "3": "06:00", "5": "06:00" });
      if (first) {
        const ov = await fetch(`/api/targets/${first.id}/overrides`);
        const ovJson = (await ov.json().catch(() => [])) as Override[];
        setOverrides(Array.isArray(ovJson) ? ovJson : []);
      } else {
        setOverrides([]);
      }
      await loadAttempts();
    } catch {
      setError("Erro de rede ao carregar a automação");
    } finally {
      setLoading(false);
    }
  }, [loadAttempts]);

  useEffect(() => {
    void load();
  }, [load]);

  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, []);

  const flash = (message: string) => {
    setNotice(message);
    setError("");
    setTimeout(() => setNotice(""), 4000);
  };

  const save = async () => {
    if (!activityName.trim() || Object.keys(schedule).length === 0) {
      setError("Informe a atividade e pelo menos um dia da semana");
      return;
    }
    setBusy(true);
    try {
      const body = { activity_name: activityName.trim(), schedule };
      const res = target
        ? await fetch(`/api/targets/${target.id}`, {
            method: "PUT",
            headers: { "content-type": "application/json" },
            body: JSON.stringify(body),
          })
        : await fetch("/api/targets", {
            method: "POST",
            headers: { "content-type": "application/json" },
            body: JSON.stringify(body),
          });
      const json = (await res.json().catch(() => ({}))) as { error?: string };
      if (!res.ok) {
        setError(json.error ?? "Não foi possível salvar a regra");
        return;
      }
      flash("Regra salva — o agendador já foi reajustado");
      await load();
      onChanged();
    } finally {
      setBusy(false);
    }
  };

  const toggleEnabled = async (next: boolean) => {
    if (!target) return;
    setBusy(true);
    try {
      const res = await fetch(`/api/targets/${target.id}`, {
        method: "PUT",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({ enabled: next }),
      });
      if (!res.ok) {
        setError("Não foi possível alterar o estado da automação");
        return;
      }
      flash(next ? "Automação ativada" : "Automação pausada");
      await load();
      onChanged();
    } finally {
      setBusy(false);
    }
  };

  const removeRule = async () => {
    if (!target) return;
    setBusy(true);
    try {
      await fetch(`/api/targets/${target.id}`, { method: "DELETE" });
      flash("Regra removida");
      await load();
      onChanged();
    } finally {
      setBusy(false);
    }
  };

  const addOverride = async () => {
    if (!target) return;
    setBusy(true);
    try {
      const res = await fetch(`/api/targets/${target.id}/overrides`, {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({ date: toApiDate(overrideDate), time: overrideTime, skip: overrideSkip }),
      });
      const json = (await res.json().catch(() => ({}))) as { error?: string };
      if (!res.ok) {
        setError(json.error ?? "Não foi possível criar a exceção");
        return;
      }
      flash(overrideSkip ? "Dia marcado como sem aula" : "Horário ajustado para esta data");
      await load();
      onChanged();
    } finally {
      setBusy(false);
    }
  };

  const removeOverride = async (id: number) => {
    setBusy(true);
    try {
      await fetch(`/api/overrides/${id}`, { method: "DELETE" });
      await load();
      onChanged();
    } finally {
      setBusy(false);
    }
  };

  const tokenState = (() => {
    const exp = status.credential?.tokenExp ?? null;
    if (!status.credential?.hasToken) return { label: "sem sessão", color: "var(--cb-error)" };
    if (!exp) return { label: "sessão ativa", color: "var(--cb-success)" };
    const ms = exp * 1000 - now;
    if (ms <= 0) return { label: "expirada", color: "var(--cb-error)" };
    return { label: `expira em ${countdown(new Date(exp * 1000).toISOString(), now)}`, color: "var(--cb-success)" };
  })();

  const okCount = status.recentAttempts.filter((a) => a.status === "success").length;
  const failCount = status.recentAttempts.filter((a) => a.status === "failed" || a.status === "given_up").length;
  const enabled = Boolean(target?.enabled);

  return (
    <>
      {/* ── engine ──────────────────────────────────────────── */}
      <section className="cb-panel cb-rise">
        <div className="cb-panel__head">
          <span className="cb-panel__title">
            <Activity size={13} />
            Motor
          </span>
          <div className="cb-row" style={{ gap: "0.6rem" }}>
            <span className="cb-label" style={{ letterSpacing: "0.16em" }}>
              {enabled ? "ativa" : "pausada"}
            </span>
            <button
              type="button"
              role="switch"
              aria-checked={enabled}
              aria-label="Ativar automação"
              className="cb-switch"
              disabled={busy || !target}
              onClick={() => void toggleEnabled(!enabled)}
            />
          </div>
        </div>
        <div
          className="cb-panel__body"
          style={{ display: "grid", gap: "1rem", gridTemplateColumns: "repeat(auto-fit, minmax(11rem, 1fr))" }}
        >
          {!target && !loading ? (
            <div className="cb-empty" style={{ gridColumn: "1 / -1" }}>
              <strong>Nenhuma regra configurada</strong>
              <span>defina atividade, dias e horário abaixo para automatizar</span>
            </div>
          ) : (
            <>
              <div>
                <p className="cb-label">Próximo disparo</p>
                <p className="cb-display" style={{ fontSize: "2.2rem", color: enabled ? "var(--cb-accent)" : "var(--cb-muted)", margin: "0.15rem 0 0" }}>
                  {status.nextExecution
                    ? countdown(
                        status.nextExecution.type === "prep" ? status.nextExecution.prepAt : status.nextExecution.windowOpensAt,
                        now,
                      )
                    : "—"}
                </p>
                <p className="cb-label" style={{ marginTop: "0.2rem" }}>
                  {status.nextExecution
                    ? `${status.nextExecution.time} · ${displayDate(status.nextExecution.targetDate)}`
                    : "nenhuma janela pendente"}
                </p>
              </div>
              <div>
                <p className="cb-label">Sessão Fiti</p>
                <p style={{ margin: "0.35rem 0 0", display: "flex", alignItems: "center", gap: "0.45rem" }}>
                  <span className="cb-led" style={{ color: tokenState.color }} />
                  <span className="cb-num" style={{ fontSize: "0.9rem" }}>{tokenState.label}</span>
                </p>
                <p className="cb-label" style={{ marginTop: "0.2rem" }}>{status.credential?.email ?? "—"}</p>
              </div>
              <div>
                <p className="cb-label">Últimos disparos</p>
                <p className="cb-num" style={{ margin: "0.35rem 0 0", fontSize: "1.05rem" }}>
                  <span style={{ color: "var(--cb-success)" }}>{okCount}</span>
                  <span style={{ color: "var(--cb-subtle)" }}> ok · </span>
                  <span style={{ color: failCount > 0 ? "var(--cb-error)" : "var(--cb-muted)" }}>{failCount}</span>
                  <span style={{ color: "var(--cb-subtle)" }}> falhas</span>
                </p>
              </div>
              <div>
                <p className="cb-label">Alvo</p>
                <p className="cb-display" style={{ fontSize: "1.35rem", margin: "0.3rem 0 0" }}>
                  {target?.activity_name ?? "—"}
                </p>
                <p className="cb-label" style={{ marginTop: "0.2rem" }}>
                  {target
                    ? Object.entries(schedule)
                        .sort(([a], [b]) => a.localeCompare(b))
                        .map(([d, t]) => `${WEEKDAYS.find((w) => w.key === d)?.short ?? d} ${t}`)
                        .join(" · ")
                    : "sem regra"}
                </p>
              </div>
            </>
          )}
        </div>
      </section>

      {(notice || error) && (
        <p
          className="cb-fade"
          style={{
            margin: 0,
            fontFamily: "var(--font-mono)",
            fontSize: "0.75rem",
            color: error ? "var(--cb-error)" : "var(--cb-accent)",
          }}
        >
          {error || notice}
        </p>
      )}

      <div className="cb-grid-2">
        {/* ── rule ──────────────────────────────────────────── */}
        <section className="cb-panel cb-rise-1">
          <div className="cb-panel__head">
            <span className="cb-panel__title">
              <TargetIcon size={13} />
              Regra de check-in
            </span>
            {target && (
              <button type="button" className="cb-btn cb-btn--ghost cb-btn--sm" disabled={busy} onClick={() => void removeRule()}>
                <Trash2 size={12} />
                Remover
              </button>
            )}
          </div>
          <div className="cb-panel__body" style={{ display: "grid", gap: "1.1rem" }}>
            <div>
              <label className="cb-label" htmlFor="cb-activity">
                Atividade
              </label>
              <input
                id="cb-activity"
                className="cb-input"
                style={{ marginTop: "0.35rem" }}
                value={activityName}
                placeholder="Ex.: CROSS"
                onChange={(e) => setActivityName(e.target.value)}
              />
              <p className="cb-label" style={{ marginTop: "0.35rem", textTransform: "none", letterSpacing: "0.02em" }}>
                precisa bater com o nome exibido na agenda da academia
              </p>
            </div>

            <div>
              <p className="cb-label">Dias e horários</p>
              <div style={{ display: "grid", gap: "0.45rem", marginTop: "0.5rem" }}>
                {WEEKDAYS.map((day) => {
                  const on = day.key in schedule;
                  return (
                    <div key={day.key} style={{ display: "grid", gridTemplateColumns: "4.5rem 1fr", alignItems: "center", gap: "0.6rem" }}>
                      <button
                        type="button"
                        className="cb-daytoggle"
                        aria-pressed={on}
                        onClick={() => {
                          const next = { ...schedule };
                          if (on) delete next[day.key];
                          else next[day.key] = Object.values(schedule)[0] ?? "06:00";
                          setSchedule(next);
                        }}
                      >
                        {day.short}
                      </button>
                      <input
                        type="time"
                        className="cb-input"
                        value={schedule[day.key] ?? ""}
                        disabled={!on}
                        onChange={(e) => setSchedule({ ...schedule, [day.key]: e.target.value })}
                        style={{ opacity: on ? 1 : 0.35 }}
                      />
                    </div>
                  );
                })}
              </div>
            </div>

            <button type="button" className="cb-btn cb-btn--primary" disabled={busy} onClick={() => void save()}>
              <Save size={13} />
              Salvar regra
            </button>
          </div>
        </section>

        {/* ── exceptions ────────────────────────────────────── */}
        <section className="cb-panel cb-rise-2">
          <div className="cb-panel__head">
            <span className="cb-panel__title">
              <AlertTriangle size={13} />
              Exceções
            </span>
            <span className="cb-label">{overrides.length}</span>
          </div>
          <div className="cb-panel__body" style={{ display: "grid", gap: "0.9rem" }}>
            <div style={{ display: "grid", gap: "0.7rem" }}>
              <DateTimePicker
                date={overrideDate}
                time={overrideTime}
                onDateChange={setOverrideDate}
                onTimeChange={setOverrideTime}
                dateLabel="Data"
                timeLabel="Horário"
                dateId="cb-override-date"
                timeId="cb-override-time"
              />
              <div className="cb-row" style={{ gap: "0.6rem" }}>
                <button
                  type="button"
                  role="switch"
                  aria-checked={overrideSkip}
                  className="cb-switch"
                  aria-label="Sem aula neste dia"
                  onClick={() => setOverrideSkip((v) => !v)}
                />
                <span className="cb-label" style={{ letterSpacing: "0.12em" }}>
                  sem aula neste dia
                </span>
                <div className="cb-spacer" />
                <button type="button" className="cb-btn cb-btn--sm" disabled={busy || !target} onClick={() => void addOverride()}>
                  <Plus size={12} />
                  Adicionar
                </button>
              </div>
            </div>

            {overrides.length === 0 ? (
              <div className="cb-empty">nenhuma exceção — a regra semanal vale sempre</div>
            ) : (
              <div>
                {overrides.map((o) => (
                  <div className="cb-logrow" key={o.id}>
                    <span className="cb-num" style={{ fontSize: "0.8rem" }}>{displayDate(o.date)}</span>
                    <span className="cb-label" style={{ letterSpacing: "0.1em" }}>
                      {o.skip ? "sem aula" : `às ${o.time}`}
                    </span>
                    <button
                      type="button"
                      className="cb-btn cb-btn--ghost cb-btn--sm"
                      disabled={busy}
                      onClick={() => void removeOverride(o.id)}
                      aria-label="Remover exceção"
                    >
                      <Trash2 size={12} />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </section>
      </div>

      {/* ── history ─────────────────────────────────────────── */}
      <section className="cb-panel cb-rise-3">
        <div className="cb-panel__head">
          <span className="cb-panel__title">
            <Power size={13} />
            Histórico de disparos
          </span>
          <button type="button" className="cb-btn cb-btn--ghost cb-btn--sm" onClick={() => void loadAttempts()}>
            Atualizar
          </button>
        </div>
        {loading && attempts.length === 0 ? (
          <div className="cb-empty">carregando histórico…</div>
        ) : attempts.length === 0 ? (
          <div className="cb-empty">nenhum disparo registrado ainda</div>
        ) : (
          attempts.map((a) => (
            <div className="cb-class" key={a.id}>
              <div>
                <p className="cb-display" style={{ fontSize: "1.15rem", margin: 0 }}>
                  {weekdayShort(a.target_date)}
                </p>
                <p className="cb-label">{displayDate(a.target_date)}</p>
              </div>
              <div style={{ minWidth: 0, display: "grid", gap: "0.3rem" }}>
                <div className="cb-row" style={{ gap: "0.45rem" }}>
                  <AttemptPill status={a.status} />
                  <span className="cb-label" style={{ letterSpacing: "0.08em" }}>{a.activity_name}</span>
                  {retryLabel(a) && <span className="cb-chip cb-chip--ghost">{retryLabel(a)}</span>}
                </div>
                <p className="cb-label" style={{ textTransform: "none", letterSpacing: "0.02em" }}>
                  {stampTime(a.started_at)} · {formatDuration(a.duration_ms)}
                  {a.error ? ` · ${a.error.slice(0, 90)}` : a.response_summary ? ` · ${a.response_summary.slice(0, 90)}` : ""}
                </p>
              </div>
              <KeyRound size={13} style={{ color: "var(--cb-subtle)" }} />
            </div>
          ))
        )}
      </section>
    </>
  );
}
